/**
 * OSHomePage v3 — RunAI Claw (Ana Komuta Merkezi)
 * Design: Premium Mac+DOS Hybrid
 * Glassmorphism, Neon Highlights, Matrix-style DOS Kernel terminal.
 */
import axios from 'axios'
import {
  Activity, Brain, Command, Cpu, Hash,
  Network, Send, Terminal as TerminalIcon, Sparkles, Zap
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { API_URL } from '../../config/api'


// ─── Interfaces ───────────────────────────────────────────────────────────────
interface Agent { id: number; agent_name: string; status: string; expertise?: string }
interface Skill { id: number; name: string; category: string; description?: string; color?: string; enabled?: boolean }
interface ActivityItem { id: number; type: string; description: string; created_at: string; earned_flz?: number }
interface ChatBubble { role: 'user' | 'assistant'; content: string; ts: string }

const authHdr = () => ({ Authorization: `Bearer ${localStorage.getItem('token') ?? ''}` })

async function apiFetch<T>(path: string): Promise<T> {
  const res = await axios.get<T>(`${API_URL}${path}`, { headers: authHdr() })
  return res.data
}

// ─── Shared Theme Utilities ───────────────────────────────────────────────────
const glassPanel = {
  background: 'rgba(17, 21, 33, 0.65)',
  backdropFilter: 'blur(16px)',
  WebkitBackdropFilter: 'blur(16px)',
  border: '1px solid rgba(255, 255, 255, 0.08)',
  boxShadow: '0 12px 40px rgba(0, 0, 0, 0.4)',
  borderRadius: 16,
}
const now = () => new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })

// ─── Subcomponents ────────────────────────────────────────────────────────────

// 1. TOP BAR: Flz Family Hub
function FlzFamilyHub({ agents }: { agents: Agent[] }) {
  // Prioritize Anne(1) and Baba(2), then other experts
  const family = agents.filter(a => [1, 2].includes(a.id)).sort((a,b) => a.id - b.id)
  const experts = agents.filter(a => ![1, 2].includes(a.id)).slice(0, 5) // Show top 5 experts
  const displayAgents = [...family, ...experts]

  return (
    <div style={{
      ...glassPanel, padding: '12px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      position: 'relative', overflow: 'hidden'
    }}>
      <div style={{ position: 'absolute', top: -30, left: '20%', width: 150, height: 150, background: 'radial-gradient(circle, rgba(99,102,241,0.15) 0%, transparent 60%)', pointerEvents: 'none' }} />
      
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ width: 40, height: 40, borderRadius: 10, background: 'linear-gradient(135deg, #6366f1, #d946ef)', display: 'flex', justifyContent: 'center', alignItems: 'center', boxShadow: '0 0 15px rgba(99,102,241,0.4)' }}>
          <Network size={20} color="#fff" />
        </div>
        <div>
          <div style={{ fontSize: 16, fontWeight: 800, color: '#fff', letterSpacing: '0.5px' }}>RunAI Claw</div>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)' }}>Otonom Komuta Merkezi</div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
        {displayAgents.map(a => (
          <div key={a.id} style={{
            display: 'flex', alignItems: 'center', gap: 8, padding: '6px 14px', borderRadius: 20,
            background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
            cursor: 'pointer', transition: 'all 0.2s', position: 'relative'
          }}
          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; e.currentTarget.style.borderColor = 'rgba(99,102,241,0.4)'; e.currentTarget.style.transform = 'translateY(-2px)' }}
          onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)'; e.currentTarget.style.transform = 'none' }}
          >
            <div style={{ 
              width: 24, height: 24, borderRadius: '50%', background: `hsl(${(a.id * 40) % 360}, 70%, 50%)`, 
              display: 'flex', justifyContent: 'center', alignItems: 'center', fontSize: 10, fontWeight: 800, color: '#fff' 
            }}>
              {a.agent_name.charAt(0)}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <span style={{ fontSize: 11, fontWeight: 600, color: '#e2e8f0' }}>{a.agent_name}</span>
              {a.expertise && <span style={{ fontSize: 9, color: '#94a3b8' }}>{a.expertise}</span>}
            </div>
            {/* Status pulsing dot */}
            <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#10b981', boxShadow: '0 0 8px #10b981', animation: 'pulse 2s infinite' }} />
          </div>
        ))}
      </div>
    </div>
  )
}

// 2. LEFT PANEL: DOS Kernel Terminal
function KernelTerminal({ activities }: { activities: ActivityItem[] }) {
  const [logs, setLogs] = useState<string[]>(['[KERNEL] RunAI OS Boot Sequence Initiated...', `[KERNEL] FlzHub connection established at ${now()}`])
  const endRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    // Generate simulated incoming activity logs
    if (activities.length > 0) {
      const newLogs = activities.slice(0, 10).map(act => `[${new Date(act.created_at).toLocaleTimeString()}] SYS_${act.type.toUpperCase()}: ${act.description}`)
      setLogs(prev => [...prev, ...newLogs])
    }
  }, [activities])

  useEffect(() => {
    const interval = setInterval(() => {
      const liveEvents = ['[PING] Ajan düğümleri canlı...', '[MEM] R2 Optimizasyonu 0.4ms...', '[NET] Cloudflare Worker PING 12ms', '[LLM] Qwen local sunucusu yanıt veriyor.']
      const ev = liveEvents[Math.floor(Math.random() * liveEvents.length)]
      setLogs(p => [...p.slice(-40), `[${now()}] ${ev}`])
    }, 5000)
    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [logs])

  return (
    <div style={{ ...glassPanel, display: 'flex', flexDirection: 'column', overflow: 'hidden', height: '100%', background: '#050510' }}>
      <div style={{ padding: '10px 16px', background: 'rgba(255,255,255,0.05)', borderBottom: '1px solid rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', gap: 6 }}>
        <TerminalIcon size={14} color="#10b981" />
        <span style={{ fontSize: 11, fontWeight: 700, color: '#10b981', letterSpacing: '1px' }}>SYS.KERNEL v2.4</span>
      </div>
      <div style={{ padding: 16, overflowY: 'auto', flex: 1, fontFamily: '"Fira Code", "Courier New", monospace', fontSize: 11, color: '#10b981', letterSpacing: '0.2px', scrollbarWidth: 'none' }}>
        {logs.map((log, i) => (
          <div key={i} style={{ marginBottom: 4, opacity: 0.8 }}>
            <span style={{ color: '#059669' }}>&gt; </span>{log}
          </div>
        ))}
        <div ref={endRef} />
      </div>
    </div>
  )
}

// 3. CENTER PANEL: Main Interactive Chat (Glassmorphism AskAI)
function MainChatArea() {
  const [chat, setChat] = useState<ChatBubble[]>([{ role: 'assistant', content: 'Merkez Komuta Aktif. Sistem loglarını sol panelden izleyebilir, yetenekleri sağ panelden yönetebilirsiniz. Size nasıl yardımcı olabilirim?', ts: now() }])
  const [input, setInput] = useState('')
  const [thinking, setThinking] = useState(false)
  const bottomRef = useRef<HTMLDivElement>(null)

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }) }, [chat])

  const send = async () => {
    const txt = input.trim()
    if (!txt) {return}
    setChat(p => [...p, { role: 'user', content: txt, ts: now() }])
    setInput('')
    setThinking(true)
    try {
      const res = await axios.post(`${API_URL}/api/ask-ai`, { query: txt }, { headers: authHdr() }).catch(() => null)
      if (res?.data?.answer) {
        setChat(p => [...p, { role: 'assistant', content: res.data.answer, ts: now() }])
      } else {
        setChat(p => [...p, { role: 'assistant', content: 'Üzgünüm, komuta merkezine bağlanılamadı.', ts: now() }])
      }
    } catch {
       setChat(p => [...p, { role: 'assistant', content: 'Beklenmeyen ağ hatası.', ts: now() }])
    }
    setThinking(false)
  }

  return (
    <div style={{ ...glassPanel, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <div style={{ padding: '16px 24px', borderBottom: '1px solid rgba(255,255,255,0.08)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Sparkles size={18} color="#a855f7" />
          <h2 style={{ fontSize: 16, fontWeight: 800, margin: 0, color: '#e2e8f0' }}>Main Intel</h2>
        </div>
        <div style={{ fontSize: 11, background: 'rgba(99,102,241,0.2)', color: '#a5b4fc', padding: '4px 10px', borderRadius: 12, border: '1px solid rgba(99,102,241,0.3)' }}>
          Gen AI Connected
        </div>
      </div>
      
      <div style={{ flex: 1, overflowY: 'auto', padding: 24, display: 'flex', flexDirection: 'column', gap: 16 }}>
        {chat.map((b, i) => (
          <div key={i} style={{ display: 'flex', justifyContent: b.role === 'user'? 'flex-end' : 'flex-start' }}>
            <div style={{ maxWidth: '80%', display: 'flex', gap: 12, flexDirection: b.role === 'user' ? 'row-reverse' : 'row' }}>
              <div style={{ width: 32, height: 32, borderRadius: '50%', background: b.role === 'user' ? '#6366f1' : 'linear-gradient(135deg, #10b981, #059669)', display: 'flex', justifyContent: 'center', alignItems: 'center', flexShrink: 0, boxShadow: '0 4px 10px rgba(0,0,0,0.3)' }}>
                {b.role === 'user' ? <Command size={14} color="#fff" /> : <Brain size={14} color="#fff" />}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: b.role === 'user' ? 'flex-end' : 'flex-start' }}>
                <div style={{
                  padding: '12px 18px', background: b.role === 'user' ? 'rgba(99,102,241,0.9)' : 'rgba(255,255,255,0.05)',
                  border: b.role === 'user' ? 'none' : '1px solid rgba(255,255,255,0.1)',
                  borderRadius: b.role === 'user' ? '18px 4px 18px 18px' : '4px 18px 18px 18px',
                  color: '#fff', fontSize: 13, lineHeight: 1.6, boxShadow: '0 4px 15px rgba(0,0,0,0.2)'
                }}>
                  {b.content}
                </div>
                <div style={{ fontSize: 10, color: '#64748b', marginTop: 4 }}>{b.ts}</div>
              </div>
            </div>
          </div>
        ))}
        {thinking && <div style={{ color: '#a855f7', fontSize: 12, display: 'flex', alignItems: 'center', gap: 5 }}><Cpu size={14} className="animate-spin" /> Veriler işleniyor...</div>}
        <div ref={bottomRef} />
      </div>

      <div style={{ margin: 16, padding: '8px 16px', background: 'rgba(0,0,0,0.2)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 20, display: 'flex', alignItems: 'center', gap: 12 }}>
        <Hash size={16} color="#64748b" />
        <input 
          value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => { if(e.key === 'Enter'){ e.preventDefault(); send() } }}
          placeholder="Komut veya mesaj girin..."
          style={{ flex: 1, background: 'transparent', border: 'none', color: '#fff', fontSize: 14, outline: 'none' }}
        />
        <button onClick={send} disabled={!input || thinking} style={{ width: 36, height: 36, borderRadius: '50%', background: input ? 'linear-gradient(135deg, #a855f7, #6366f1)' : 'rgba(255,255,255,0.1)', color: '#fff', border: 'none', display: 'flex', justifyContent: 'center', alignItems: 'center', cursor: input ? 'pointer' : 'default', transition: 'all 0.3s' }}>
          <Send size={16} />
        </button>
      </div>
    </div>
  )
}

// 4. RIGHT PANEL: OpenClaw Skills
function SkillsPanel({ skills, navigate }: { skills: Skill[], navigate: any }) {
  if (skills.length === 0) {return (
    <div style={{ ...glassPanel, padding: 24, textAlign: 'center', color: '#94a3b8' }}>
       No skills installed.
    </div>
  )}

  return (
    <div style={{ ...glassPanel, display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <div style={{ padding: '16px 20px', borderBottom: '1px solid rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', gap: 10 }}>
        <Zap size={16} color="#f59e0b" />
        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#e2e8f0' }}>OpenClaw Skills</h3>
      </div>
      <div style={{ padding: 16, overflowY: 'auto', display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 12 }}>
        {skills.map(s => {
          const bg = s.color || '#6366f1'
          return (
            <div key={s.id} onClick={() => navigate(`/dashboard/skills/${s.id}`)} style={{
              padding: '12px 16px', borderRadius: 12, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)',
              display: 'flex', alignItems: 'center', gap: 14, cursor: 'pointer', transition: 'all 0.2s', position: 'relative', overflow: 'hidden'
            }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; e.currentTarget.style.borderColor = bg; e.currentTarget.style.transform = 'translateX(4px)' }}
            onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.03)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.06)'; e.currentTarget.style.transform = 'none' }}
            >
              <div style={{ width: 36, height: 36, borderRadius: 10, background: `linear-gradient(135deg, ${bg}dd, ${bg}66)`, display: 'flex', justifyContent: 'center', alignItems: 'center', boxShadow: `0 4px 15px ${bg}40`, flexShrink: 0 }}>
                <Activity size={16} color="#fff" />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2, overflow: 'hidden' }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#fff', whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>{s.name}</div>
                <div style={{ fontSize: 10, color: '#94a3b8' }}>{s.category}</div>
              </div>
              <div style={{ marginLeft: 'auto', width: 8, height: 8, borderRadius: '50%', background: s.enabled ? '#10b981' : '#64748b', boxShadow: s.enabled ? '0 0 10px #10b981' : 'none' }} />
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
export default function OSHomePage() {
  const navigate = useNavigate()
  // user not used in this component
  
  const [agents, setAgents] = useState<Agent[]>([])
  const [skills, setSkills] = useState<Skill[]>([])
  const [activity, setActivity] = useState<ActivityItem[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    document.documentElement.style.setProperty('--body-bg', '#05070e') // Deep dark override for Claw
    const load = async () => {
      setLoading(true)
      const [agentsRes, skillsRes, activityRes] = await Promise.allSettled([
        apiFetch<Agent[] | { agents: Agent[] }>('/api/agents'),
        apiFetch<Skill[] | { skills: Skill[] }>('/api/aicloud/skills/installed'),
        apiFetch<ActivityItem[] | { activities: ActivityItem[] }>('/api/users/me/activity'),
      ])
      
      if (agentsRes.status === 'fulfilled') {
        const v = agentsRes.value
        setAgents(Array.isArray(v) ? v : ((v as any).agents ?? []))
      }
      if (skillsRes.status === 'fulfilled') {
        const v = skillsRes.value
        setSkills(Array.isArray(v) ? v : ((v as any).skills ?? []))
      }
      if (activityRes.status === 'fulfilled') {
        const v = activityRes.value
        setActivity(Array.isArray(v) ? v : ((v as any).activities ?? []))
      }
      setLoading(false)
    }
    void load()

    return () => { document.documentElement.style.removeProperty('--body-bg') }
  }, [])

  if (loading) {return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#05070e' }}>
      <Network size={40} color="#a855f7" className="animate-pulse" style={{ marginBottom: 16 }} />
      <div style={{ color: '#a855f7', fontSize: 13, letterSpacing: '2px', fontWeight: 600 }}>BOOTING RUNAI CLAW...</div>
    </div>
  )}

  return (
    <div style={{ 
      padding: '20px', display: 'flex', flexDirection: 'column', gap: 20, 
      height: '100%', overflow: 'hidden', background: '#05070e', color: '#fff',
      fontFamily: '"Inter", sans-serif'
    }}>
      {/* Dynamic Background Effects */}
      <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', overflow: 'hidden', pointerEvents: 'none', zIndex: 0 }}>
        <div style={{ position: 'absolute', top: -200, right: -100, width: 600, height: 600, background: 'radial-gradient(circle, rgba(168,85,247,0.05) 0%, transparent 70%)', borderRadius: '50%' }} />
        <div style={{ position: 'absolute', bottom: -300, left: -200, width: 800, height: 800, background: 'radial-gradient(circle, rgba(99,102,241,0.05) 0%, transparent 60%)', borderRadius: '50%' }} />
      </div>

      <div style={{ zIndex: 1, display: 'flex', flexDirection: 'column', gap: 20, height: '100%' }}>
        <FlzFamilyHub agents={agents} />
        
        <div style={{ display: 'flex', flex: 1, gap: 20, minHeight: 0 }}>
          {/* DOS Kernel Terminal Panel */}
          <div style={{ width: '25%', minWidth: 260 }}>
            <KernelTerminal activities={activity} />
          </div>
          
          {/* Central Main Chat Panel */}
          <div style={{ flex: 1 }}>
            <MainChatArea />
          </div>
          
          {/* Right Skills Panel */}
          <div style={{ width: '22%', minWidth: 240 }}>
            <SkillsPanel skills={skills} navigate={navigate} />
          </div>
        </div>
      </div>
    </div>
  )
}
